import {
  GENERATING_CREATIVE_TITLE,
  GENERATING_VIDEO_MESSAGE,
  GENERATING_CUSTOM_REPORT_MESSAGE,
  ANALYZING_CREATIVE_TEMPLATE_MESSAGE,
  PRODUCING_CREATIVE_TEMPLATE_MESSAGE,
  MODAL_CREATE_WITH_IA_GENERATING_IMAGES,
  RESIZING_IMAGES_BATCH_MESSAGE,
  ANALYZING_IMAGE_LAYERS_MESSAGE,
} from "../i18n/keysTranslations";
import { ALERTS_ASYNC_STATUS } from "./constants";
import {
  CREATIVE_JOB_IN_PROGRESS_STATUSES,
  CREATIVE_JOB_STATUS,
  CREATIVE_JOB_TERMINAL_STATUSES,
} from "./creativeJobStatus";
import { CREATIVE_JOB_TYPES, errorCodeToText } from "./errors";
import { getCreativeJobProgress } from "./creativeJobProgressStorage";
import { getCustomReportSourcePath } from "./customReportSource";

export const CREATIVE_JOB_RTDB_SOURCE = "creativeJobRtdb";

/**
 * Job types hydrated into the global alerts panel from RTDB.
 * Add new types here when their alert UX is ready.
 */
export const CREATIVE_JOB_ALERT_ENABLED_TYPES = new Set([
  CREATIVE_JOB_TYPES.generateImages,
  CREATIVE_JOB_TYPES.generateVideo,
  CREATIVE_JOB_TYPES.resizeImagesBatch,
  CREATIVE_JOB_TYPES.generateCustomReport,
  CREATIVE_JOB_TYPES.analyzeImageLayers,
  CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
  CREATIVE_JOB_TYPES.produceCreativeTemplate,
]);

const getMediaTypeFromJobType = (jobType) => {
  if (jobType === CREATIVE_JOB_TYPES.generateVideo) {
    return "video";
  }

  return "image";
};

export const normalizeCreativeJobs = (snapshotVal) =>
  Object.entries(snapshotVal || {})
    .map(([jobID, job]) => ({ jobID, ...job }))
    .sort((a, b) => (b.creationTime || 0) - (a.creationTime || 0));

export const getCreativeJobAlertMessageKey = ({ jobType }) => {
  switch (jobType) {
    case CREATIVE_JOB_TYPES.generateImages:
      return MODAL_CREATE_WITH_IA_GENERATING_IMAGES;
    case CREATIVE_JOB_TYPES.generateVideo:
      return GENERATING_VIDEO_MESSAGE;
    case CREATIVE_JOB_TYPES.resizeImagesBatch:
      return RESIZING_IMAGES_BATCH_MESSAGE;
    case CREATIVE_JOB_TYPES.generateCustomReport:
      return GENERATING_CUSTOM_REPORT_MESSAGE;
    case CREATIVE_JOB_TYPES.analyzeImageLayers:
      return ANALYZING_IMAGE_LAYERS_MESSAGE;
    case CREATIVE_JOB_TYPES.analyzeCreativeTemplate:
      return ANALYZING_CREATIVE_TEMPLATE_MESSAGE;
    case CREATIVE_JOB_TYPES.produceCreativeTemplate:
      return PRODUCING_CREATIVE_TEMPLATE_MESSAGE;
    default:
      return GENERATING_CREATIVE_TITLE;
  }
};

export const mapJobTypeToNotificationType = (jobType) => {
  if (jobType === CREATIVE_JOB_TYPES.generateCustomReport) {
    return "generatingCustomReport";
  }

  if (jobType === CREATIVE_JOB_TYPES.analyzeImageLayers) {
    return "analyzingImageLayers";
  }
  if (jobType === CREATIVE_JOB_TYPES.analyzeCreativeTemplate) {
    return "analyzingCreativeTemplate";
  }
  if (jobType === CREATIVE_JOB_TYPES.produceCreativeTemplate) {
    return "producingCreativeTemplate";
  }

  return "generatingCreative";
};

export const mapJobStatusToAlertStatus = (status) => {
  if (status === CREATIVE_JOB_STATUS.SUCCESS) {
    return ALERTS_ASYNC_STATUS.SUCCESS;
  }
  if (status === CREATIVE_JOB_STATUS.ERROR) {
    return ALERTS_ASYNC_STATUS.ERROR;
  }
  if (status === CREATIVE_JOB_STATUS.CANCELLED) {
    return ALERTS_ASYNC_STATUS.ERROR;
  }
  if (CREATIVE_JOB_IN_PROGRESS_STATUSES.has(status)) {
    return ALERTS_ASYNC_STATUS.LOADING;
  }

  return ALERTS_ASYNC_STATUS.LOADING;
};

const isUsableResultPayload = (payload) => {
  if (!payload || typeof payload !== "object") return false;

  if (payload.kind === "images") {
    return Array.isArray(payload.images) && payload.images.length > 0;
  }

  if (payload.kind === "video") {
    return Array.isArray(payload.videos) && payload.videos.length > 0;
  }

  if (payload.kind === "url") {
    return Boolean(payload.url);
  }

  if (payload.kind === "layers") {
    return Boolean(payload.sourceUrl && payload.urlHash);
  }

  if (payload.kind === "template") {
    return Boolean(payload.templateID);
  }

  // Legacy payloads without kind
  return Boolean(
    payload.url ||
      (Array.isArray(payload.images) && payload.images.length > 0) ||
      (Array.isArray(payload.videos) && payload.videos.length > 0)
  );
};

/**
 * Creative result lives in response.data:
 * { ok: true, data: { kind: "images"|"video"|"url", ... } }
 */
const getJobResultPayload = (job) => job?.response?.data || null;

const hasJobResultData = (job) =>
  isUsableResultPayload(getJobResultPayload(job));

export const isCreativeJobAlertEnabled = (jobType) =>
  CREATIVE_JOB_ALERT_ENABLED_TYPES.has(jobType);

export const isVisibleCreativeJob = (job) => {
  if (!job?.jobID) return false;
  if (!isCreativeJobAlertEnabled(job.jobType)) return false;
  if (job.status === CREATIVE_JOB_STATUS.CANCELLED) return false;

  if (CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job.status)) return true;
  if (job.status === CREATIVE_JOB_STATUS.ERROR) return true;

  if (job.status === CREATIVE_JOB_STATUS.SUCCESS) {
    if (job.jobType === CREATIVE_JOB_TYPES.produceCreativeTemplate) {
      return Boolean(job.payloadSnapshot?.produceID) || hasJobResultData(job);
    }
    return hasJobResultData(job);
  }

  return false;
};

const mapImagesResultToGeneratedMedia = (images = []) => {
  const newImages = [];
  const newRatings = {};
  const carouselsById = {};
  const carouselOrder = [];

  images.forEach((image) => {
    if (!image?.url) return;
    newRatings[image.url] = image.rating;

    if (image.carouselID) {
      if (!carouselsById[image.carouselID]) {
        carouselsById[image.carouselID] = [];
        carouselOrder.push(image.carouselID);
      }
      carouselsById[image.carouselID].push(image.url);
      return;
    }

    newImages.push(image.url);
  });

  const carouselItems = carouselOrder.map((carouselID) => ({
    type: "carousel",
    carouselID,
    urls: carouselsById[carouselID],
    rating: null,
  }));

  return {
    generatedMedia: [...carouselItems, ...newImages],
    ratings: newRatings,
  };
};

const mapVideosResultToGeneratedMedia = (videos = []) =>
  videos
    .map((video) => {
      if (typeof video === "string") return video;
      return video?.url || null;
    })
    .filter(Boolean);

export const mapJobResultToGenerationData = ({ job, payloadSnapshot = {} }) => {
  if (job?.jobType === CREATIVE_JOB_TYPES.generateCustomReport) {
    const result = getJobResultPayload(job);
    const isInProgress = CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job?.status);

    return {
      ...payloadSnapshot,
      modalType: "customReport",
      customPrompt: payloadSnapshot.customPrompt || "",
      sourcePath:
        payloadSnapshot.sourcePath ||
        getCustomReportSourcePath({ campaignID: payloadSnapshot.campaignID }),
      format: result?.format || payloadSnapshot.format,
      fileName: result?.fileName || payloadSnapshot.fileName,
      downloadUrl: result?.downloadUrl || result?.url || null,
      isGenerating: isInProgress,
    };
  }

  if (job?.jobType === CREATIVE_JOB_TYPES.analyzeImageLayers) {
    const result = getJobResultPayload(job);
    const isInProgress = CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job?.status);

    return {
      ...payloadSnapshot,
      modalType: "imageEditLayers",
      sourceUrl: result?.sourceUrl || payloadSnapshot.url,
      urlHash: result?.urlHash || payloadSnapshot.urlHash,
      isGenerating: isInProgress,
    };
  }

  if (job?.jobType === CREATIVE_JOB_TYPES.analyzeCreativeTemplate) {
    const result = getJobResultPayload(job);

    return {
      ...payloadSnapshot,
      templateID: result?.templateID || payloadSnapshot.templateID,
      layerCount: result?.layerCount || payloadSnapshot.layerCount || 0,
      isGenerating: CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job?.status),
    };
  }

  if (job?.jobType === CREATIVE_JOB_TYPES.produceCreativeTemplate) {
    const result = getJobResultPayload(job);

    return {
      ...payloadSnapshot,
      templateID: result?.templateID || payloadSnapshot.templateID,
      produceID: result?.produceID || payloadSnapshot.produceID,
      language: result?.language || payloadSnapshot.language,
      itemCount: payloadSnapshot.itemCount,
      isGenerating: CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job?.status),
    };
  }

  const result = getJobResultPayload(job);
  const baseGenerationData = {
    ...payloadSnapshot,
    mediaType:
      payloadSnapshot.mediaType || getMediaTypeFromJobType(job?.jobType),
    modalType: payloadSnapshot.modalType || "createWithIA",
    isGenerating: CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job?.status),
  };

  if (!result || !CREATIVE_JOB_TERMINAL_STATUSES.has(job?.status)) {
    return baseGenerationData;
  }

  if (result.kind === "images" || Array.isArray(result.images)) {
    const { generatedMedia, ratings } = mapImagesResultToGeneratedMedia(
      result.images || []
    );

    return {
      ...baseGenerationData,
      isGenerating: false,
      generatedMedia: [
        ...generatedMedia,
        ...(payloadSnapshot.generatedMedia || []),
      ],
      ratings: {
        ...(payloadSnapshot.ratings || {}),
        ...ratings,
      },
    };
  }

  if (result.kind === "video" || Array.isArray(result.videos)) {
    return {
      ...baseGenerationData,
      isGenerating: false,
      generatedMedia: [
        ...mapVideosResultToGeneratedMedia(result.videos || []),
        ...(payloadSnapshot.generatedMedia || []),
      ],
    };
  }

  if (result.kind === "url" || result.url || result.urls?.[0]) {
    const resultUrl = result.url || result.urls?.[0];

    if (resultUrl) {
      return {
        ...baseGenerationData,
        isGenerating: false,
        generatedMedia: [resultUrl, ...(payloadSnapshot.generatedMedia || [])],
      };
    }
  }

  return {
    ...baseGenerationData,
    isGenerating: false,
  };
};

const resolveJobErrorMessage = (job) => {
  if (job.status !== CREATIVE_JOB_STATUS.ERROR) {
    return undefined;
  }

  const code = job.response?.code;
  if (code) {
    const translated = errorCodeToText(code);
    if (translated && translated !== code) {
      return translated;
    }
  }

  return job.response?.message;
};

export const mapCreativeJobToNotification = (job) => {
  const payloadSnapshot = job?.payloadSnapshot || {};
  const alertStatus = mapJobStatusToAlertStatus(job.status);
  const isTerminal = CREATIVE_JOB_TERMINAL_STATUSES.has(job.status);

  return {
    id: job.jobID,
    jobID: job.jobID,
    source: CREATIVE_JOB_RTDB_SOURCE,
    type: mapJobTypeToNotificationType(job.jobType),
    status: alertStatus,
    progress: isTerminal ? 100 : getCreativeJobProgress(job.jobID),
    jobType: job.jobType,
    timestamp: job.creationTime || job.updateTime || Date.now(),
    messageKey: getCreativeJobAlertMessageKey({ jobType: job.jobType }),
    message: resolveJobErrorMessage(job),
    generationData: mapJobResultToGenerationData({
      job,
      payloadSnapshot,
    }),
  };
};

export const mapVisibleCreativeJobsToNotifications = (jobs = []) =>
  jobs.filter(isVisibleCreativeJob).map(mapCreativeJobToNotification);

const CREATIVE_NOTIFICATION_TYPES = new Set([
  "generatingCreative",
  "imageAnimation",
  "generatingCustomReport",
  "analyzingImageLayers",
  "analyzingCreativeTemplate",
  "producingCreativeTemplate",
]);

export const isCreativeNotification = (notification) => {
  if (!notification) return false;
  if (notification.source === CREATIVE_JOB_RTDB_SOURCE) return true;

  return (
    CREATIVE_NOTIFICATION_TYPES.has(notification.type) &&
    Boolean(notification.jobID)
  );
};
